import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import { DateRangePicker } from "react-dates";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";

import * as api from "services/api";

import LoadingComponent from "components/LoadingComponent";
import { TextInput } from "components/TextInput";
import { SelectInput } from "components/SelectInput";
import { StyledTable } from "components/StyledTable";

import {
	ContractSolicitationPriority,
	ContractSolicitationStatus,
	PRIORITY_OPTIONS
} from "types/contractSolicitation";

import { formatRelativeTime } from "helpers/date";

import ls from "Localization";

import {
	ButtonsContainer,
	DateRangePickerContainer,
	FilterContainer,
	FiltersContainer,
	FilterTitle,
	InputsContainer,
	MainLink,
	PriorityBadge,
	RowContainer,
	StatusBadge,
	TableContainer,
	Title
} from "./styles";

const tableColumns = [
	{
		field: "solicitationId",
		headerName: <span>Solicitação</span>,
		align: "left",
		alignContent: "left",
		width: 130,
		content: value => <span>{`#${String(value).padStart(6, "0")}`}</span>
	},
	{
		field: "priority",
		headerName: <span>Prioridade</span>,
		align: "left",
		alignContent: "left",
		width: 130,
		content: value =>
			value ? (
				<PriorityBadge
					variant={
						value === ContractSolicitationPriority.Urgent
							? "#8b0000"
							: value === ContractSolicitationPriority.High
							? "#fb2c36"
							: value === ContractSolicitationPriority.Medium
							? "#fe9400"
							: "#00c950"
					}
				>
					<span>{(ls[value] || value).toUpperCase()}</span>
				</PriorityBadge>
			) : (
				<span></span>
			)
	},
	{
		field: "createdAt",
		headerName: <span>Data de solicitação</span>,
		align: "left",
		alignContent: "left",
		width: 150,
		content: value => <span>{dayjs(value).format("DD/MM/YYYY")}</span>
	},
	{
		field: "hired.businessName",
		headerName: <span>Prestador</span>,
		align: "left",
		alignContent: "left",
		width: 280,
		content: value => <span>{value}</span>
	},
	{
		field: "hirer.businessName",
		headerName: <span>Contratante</span>,
		align: "left",
		alignContent: "left",
		width: 280,
		content: value => <span>{value}</span>
	},
	{
		field: "tier",
		headerName: <span>Tier</span>,
		align: "left",
		alignContent: "left",
		width: 130,
		content: value => (
			<span>
				{value
					? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
					: ""}
			</span>
		)
	},
	{
		field: "contractType",
		headerName: <span>Tipo de contrato</span>,
		align: "left",
		alignContent: "left",
		width: 180,
		content: value => <span>{ls[value]}</span>
	},
	{
		field: "status",
		headerName: <span>Status</span>,
		align: "left",
		alignContent: "left",
		width: 150,
		content: value => (
			<StatusBadge
				variant={
					value === ContractSolicitationStatus.Requested
						? "#00bba7"
						: value === ContractSolicitationStatus.Production
						? "#f0b100"
						: value === ContractSolicitationStatus.Adjustment
						? "#ff6900"
						: value === ContractSolicitationStatus.Validation
						? "#ad46ff"
						: value === ContractSolicitationStatus.Creation
						? "#2b7fff"
						: "#497d00"
				}
			>
				<span>{ls[value].toUpperCase()}</span>
			</StatusBadge>
		)
	},
	{
		field: "createdBy.name",
		headerName: <span>Solicitante</span>,
		align: "left",
		alignContent: "left",
		width: 200,
		content: value => <span>{value || ""}</span>
	},
	{
		field: "updatedAt",
		headerName: <span>Última atualização</span>,
		align: "left",
		alignContent: "left",
		width: 230,
		content: value => <span>{formatRelativeTime(value)}</span>
	}
];

export default function ContractSolicitationList() {
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [focusedInput, setFocusedInput] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [skip, setSkip] = useState(0);
	const [take, setTake] = useState(10);
	const [filterUrl, setFilterUrl] = useState("");
	const [tableData, setTableData] = useState([]);
	const [count, setCount] = useState(0);

	const history = useHistory();

	const formRef = useRef(null);

	const { token } = useSelector(s => s.login);

	function handleChangeSkip(skip) {
		setSkip(skip);
	}

	function handleChangeTake({ target }) {
		setSkip(0);
		setTake(target.value);
	}

	function handleFormSubmit(event) {
		event.preventDefault();

		const formData = new FormData(formRef.current);

		let filterUrl = "";

		for (let [key, value] of formData.entries()) {
			if (key === "solicitationId" && value) {
				const cleanValue = value.replace("#SC", "");

				const numericValue = parseInt(cleanValue, 10).toString();
				value = numericValue;
			}

			if (key === "updatedAt" && value) {
				const [days] = value.split("d");

				const date = dayjs()
					.subtract(days, "day")
					.format("DD/MM/YYYY");
				value = date;
			}

			if (value) {
				filterUrl += `&filters[${key}]=${value}`;
			}
		}

		setSkip(0);
		setFilterUrl(filterUrl);
	}

	function handleClearFields() {
		formRef.current.reset();
		setStartDate(null);
		setEndDate(null);
		setFilterUrl("");
		setSkip(0);
	}

	async function fetchContractSolicitations() {
		try {
			setIsLoading(true);

			const response = await api.sendGet(
				`/contract-solicitation?skip=${skip *
					take}&take=${take}&sort=updatedAt&desc=true${filterUrl}`,
				{
					Authorization: "Bearer " + token
				}
			);

			const result = await response.json();

			if (result.isValid) {
				setTableData(result.model);
				setCount(result.params.count);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		fetchContractSolicitations();
	}, [skip, take, filterUrl]);

	return (
		<Container className="dashboard">
			<Card>
				<CardBody>
					<RowContainer>
						<Title>Solicitações de Contratos</Title>
						<MainLink to="/contracts/contract-solicitation-selection">
							Solicitar contrato
						</MainLink>
					</RowContainer>

					<FiltersContainer>
						<FilterTitle>Filtrar por:</FilterTitle>

						<form ref={formRef} onSubmit={handleFormSubmit}>
							<InputsContainer>
								<FilterContainer>
									<TextInput
										id="solicitationId"
										name="solicitationId"
										placeholder="ID da solicitação"
										label="Solicitação"
									/>
								</FilterContainer>

								<FilterContainer>
									<SelectInput
										name="priority"
										label="Prioridade"
										options={[
											{ label: "Todos", value: "" },
											...PRIORITY_OPTIONS
										]}
									/>
								</FilterContainer>

								<FilterContainer>
									<label>Data da solicitação</label>
									<DateRangePickerContainer>
										<DateRangePicker
											startDatePlaceholderText="Data Inicial"
											startDate={startDate}
											startDateId="createdAtInitDate"
											endDatePlaceholderText="Data Final"
											endDate={endDate}
											endDateId="createdAtEndDate"
											onDatesChange={({ startDate, endDate }) => {
												setStartDate(startDate);
												setEndDate(endDate);
											}}
											focusedInput={focusedInput}
											onFocusChange={focusedInput =>
												setFocusedInput(focusedInput)
											}
											small={true}
											isOutsideRange={() => false}
											noBorder={true}
											showClearDates
										/>
									</DateRangePickerContainer>
								</FilterContainer>

								<FilterContainer>
									<TextInput
										id="hired"
										name="hired.businessName"
										placeholder="Empresa prestadora"
										label="Prestador"
									/>
								</FilterContainer>

								<FilterContainer>
									<TextInput
										id="hirer"
										name="hirer.businessName"
										placeholder="Empresa contratante"
										label="Contratante"
									/>
								</FilterContainer>

								<FilterContainer>
									<SelectInput
										name="tier"
										label="Tier"
										options={[
											{ label: "Todos", value: "" },
											{ label: "High", value: "high" },
											{ label: "Mid", value: "mid" },
											{ label: "Low", value: "low" }
										]}
									/>
								</FilterContainer>

								<FilterContainer>
									<SelectInput
										name="contractType"
										label="Tipo de contrato"
										options={[
											{ label: "Todos", value: "" },
											{ label: "NDA", value: "nda" },
											{ label: "Confidencialidade", value: "confidentiality" },
											{ label: "Comodato", value: "loan" },
											{
												label: "Prestação de Serviço",
												value: "serviceProvision"
											},
											{ label: "Aditivo contratual", value: "addendum" },
											{ label: "Distrato", value: "termination" },
											{ label: "Outros", value: "other" }
										]}
									/>
								</FilterContainer>

								<FilterContainer>
									<SelectInput
										name="status"
										label="Status"
										options={[
											{ label: "Todos", value: "" },
											{ label: "Solicitado", value: "requested" },
											{ label: "Produção", value: "production" },
											{ label: "Ajustes", value: "adjustment" },
											{ label: "Validação", value: "validation" },
											{ label: "Geração", value: "creation" },
											{ label: "Finalizado", value: "finished" }
										]}
									/>
								</FilterContainer>

								<FilterContainer>
									<TextInput
										id="requester"
										name="requester"
										placeholder="Nome"
										label="Solicitante"
									/>
								</FilterContainer>

								<FilterContainer>
									<SelectInput
										name="updatedAt"
										label="Última atualização"
										options={[
											{ label: "Todos", value: "" },
											{ label: "1 dia", value: "1d" },
											{ label: "7 dias", value: "7d" },
											{ label: "14 dias", value: "14d" },
											{ label: "30 dias", value: "30d" },
											{ label: "60 dias", value: "60d" }
										]}
									/>
								</FilterContainer>
							</InputsContainer>

							<ButtonsContainer>
								<button type="button" onClick={handleClearFields}>
									Limpar
								</button>
								<button type="submit">Aplicar</button>
							</ButtonsContainer>
						</form>
					</FiltersContainer>

					<TableContainer>
						{isLoading ? (
							<LoadingComponent />
						) : (
							<StyledTable
								data={tableData}
								columns={tableColumns}
								handleChangeSkip={handleChangeSkip}
								handleChangeTake={handleChangeTake}
								skip={skip}
								take={take}
								count={count}
								showHeader
								rowClick={true}
								onRowClick={({ _id }) => {
									history.push(`/contracts/contract-solicitation/${_id}`);
								}}
							/>
						)}
					</TableContainer>
				</CardBody>
			</Card>
		</Container>
	);
}
